import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { reportsPageModule, loadFincenterActs, exportFincenterAct, generateFincenterAct } from '../ReportsPageDucks';
import { Box, Typography, Select, MenuItem, Popover, IconButton } from '@material-ui/core';
import { ROLES } from '../../../_helpers/Constants';
import StaticTablePagination from '../../../_ui/Table/StaticTablePaginated';
import { reportsFincenterActsColumn } from './ReportsFincenterActsColumn';
import { useHistory } from 'react-router-dom';
import useTableFilter from '../../../components/TableFilter/useTableFilter';
import Button from '../../../_ui/Button/Button';
import moment from 'moment';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR - 5 + i);
const DEFAULT_FILTER = { pageNumber: 1, pageSize: 20, year: CURRENT_YEAR };

const ReportsFincenterActsPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const reportFincenterActs = useSelector(state => state[reportsPageModule].reportFincenterActs);
  const loadingFincenterActs = useSelector(state => state[reportsPageModule].loadingFincenterActs);
  const user = useSelector(state => state.login.user);
  const isHead = useMemo(() => user?.roles?.includes(ROLES.ES_SERVICE_PROVIDER_HEADS), [user]);

  const [anchorEl, setAnchorEl] = useState(null);

  const { filter, changeFilter } = useTableFilter(
    filter => dispatch(loadFincenterActs(filter)),
    DEFAULT_FILTER
  );

  const handleDownloadAct = documentId => dispatch(exportFincenterAct(documentId));
  const handleGenerateAct = () => dispatch(generateFincenterAct(history));
  const handleInfoClick = event => setAnchorEl(event.currentTarget);
  const handleInfoClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const popoverId = open ? 'fincenter-info-popover' : undefined;

  const canGenerateReport = useMemo(() => {
    const today = moment();
    const startDate = moment().date(16);
    const endDate = moment().endOf('month');
    if (!today.isBetween(startDate, endDate, 'day', '[]')) return false;
    const currentMonth = today.month();
    const currentYear = today.year();
    return !reportFincenterActs.content.some(act => {
      if (!act.periodMonth) return false;
      const actDate = moment(act.periodMonth);
      return actDate.month() === currentMonth && actDate.year() === currentYear;
    });
  }, [reportFincenterActs]);

  return (
    <div>
      <Box className="flex justify-between items-center mt2">
        <Box className="flex items-center" style={{ gap: 8 }}>
          <Typography style={{ fontSize: '14px' }}>
            {t('reportsPage_fincenterActPeriod')}
          </Typography>
          <Select
            value={filter.year || CURRENT_YEAR}
            onChange={e => changeFilter({ year: e.target.value, pageNumber: 1 })}
            variant="outlined"
            style={{ width: '150px', height: '40px' }}
          >
            {YEARS.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </Box>
        <div className="flex items-center">
          {isHead && (
            <Button
              text={t('reportsPage_generateFincenterAct')}
              onClick={handleGenerateAct}
              loading={loadingFincenterActs}
              disabled={!canGenerateReport}
            />
          )}
          <IconButton aria-describedby={popoverId} onClick={handleInfoClick} color="primary">
            <InfoOutlinedIcon />
          </IconButton>
          <Popover
            id={popoverId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleInfoClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Box p={2} style={{ maxWidth: 300 }}>
              <Typography>{t('reportsPage_fincenterActInfo')}</Typography>
            </Box>
          </Popover>
        </div>
      </Box>
      <div className="mt1">
        <StaticTablePagination
          sortable={false}
          loading={loadingFincenterActs}
          columns={reportsFincenterActsColumn({ t, language: i18n.language, onDownloadAct: handleDownloadAct, history, tabType: 'generate' })}
          totalElements={reportFincenterActs.totalElements}
          data={reportFincenterActs.content}
        />
      </div>
    </div>
  );
};

export default ReportsFincenterActsPage;
